import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import _ from 'lodash';
import * as moment from 'moment';

import {
  CleanLevelList,
  TaskTypeList,
} from '@root/router/task-v2/task-v2.props';
import { DateService } from '@root/shared/service/date.service';
import { StoreService } from '@root/shared/service/store.service';
import { MAX_AMOUNT, OVERSEAS_EDITION_TIME_FORMAT } from '@root/config';
import { CreateTaskService } from '@root/router/component/task/create-task/create-task.service';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Router } from '@angular/router';
import { isArray } from 'rxjs/internal/util/isArray';

export interface ITaskDetail {
  id?: number;
  type?: number;
  house?: any;
  schedule_staff_id?: number;
  expected_date: string;
  expected_time?: string;
  level?: number;
  currency_type?: string;
  fee?: number;
  note?: string;
  gen_transaction?: number;
  photos?: string;
}
@Component({
  selector: 'host-create-task',
  templateUrl: './create-task.component.html',
  styleUrls: ['./create-task.component.scss'],
})
export class CreateTaskComponent implements OnInit {
  @Input() data: ITaskDetail = null;
  form: FormGroup = this.fb.group({
    type: 0,
    assignee: null,
    date: null,
    time: null,
    cleanLevel: 0, // 默认为标准，仅保洁任务有保洁等级,
    fee: 0,
    currency: 'CNY',
    included: false,
    payment_method: null,
    note: null,
    appendix: [[]],
  });
  typeList = [];
  assigneeList = [];
  hasCheckedHouses = [];
  checkedHouses = [];
  defaultHouseTitle;
  cleanLevelList = _.filter(CleanLevelList, function (level) {
    return level.value >= 0;
  });
  houseParam = null;
  fees = {
    standard: 0,
    simple: 0,
    advance: 0,
    currency: 'CNY',
  };
  maxAmount = MAX_AMOUNT;
  currencyList = [];
  listOfMethodOption = [];

  checking = false;
  editIniting = false;

  btnLoading = false;

  get angularDateFormat() {
    return this.dateService.getAngularFormat(['YMD']);
  }

  get angularTimeFormat() {
    return this.dateService.getAngularFormat([null, 'hm']);
  }

  get use12Hours() {
    const timeFormat = this.dateService.getTimeFormat();
    return timeFormat === OVERSEAS_EDITION_TIME_FORMAT.HOUR_TWELVE
      ? true
      : false;
  }

  constructor(
    private fb: FormBuilder,
    private dateService: DateService,
    private localService: StoreService,
    private createTaskService: CreateTaskService,
    private subject: NzModalRef,
    private router: Router
  ) {}

  ngOnInit() {
    this.getOptions();
  }

  getOptions() {
    this.getTypes();
    this.getAssignees();
    this.getCurrencyList();
    this.getPaymentMethodList();
  }

  getTypes() {
    this.typeList = _.filter(TaskTypeList, function (type) {
      return type.value >= 0;
    });
  }

  getAssignees() {
    this.createTaskService.getAllStaff().subscribe((res) => {
      if (res.error_code === 0) {
        this.assigneeList = _.get(res, 'data.list');
        this.initForm();
      }
    });
  }

  getCurrencyList() {
    this.createTaskService
      .getCleaningFeeCurrencyList({
        for_filter: 0,
      })
      .subscribe((res) => {
        if (res.error_code === 0) {
          this.currencyList = _.get(res, 'data.list');
          this.form.patchValue({
            currency: _.get(this.data, 'currency_type') || this.currencyList[0],
          });
        }
      });
  }

  getPaymentMethodList() {
    this.createTaskService.getSettings().subscribe((res) => {
      if (res.error_code === 0) {
        this.listOfMethodOption =
          _.filter(_.get(res, 'data.income_method'), { delete_time: null }) ||
          [];
      }
    });
  }

  initForm() {
    if (this.data) {
      this.editIniting = true;
      let timeValue = moment().startOf('day').add(12, 'hours').toDate();
      if (this.data.expected_time) {
        const timeTemp = _.split(this.data.expected_time, ':');
        timeValue = moment()
          .startOf('day')
          .add(timeTemp[0], 'hours')
          .add(timeTemp[1], 'minutes')
          .toDate();
      }
      const urls = [];
      if (this.data.photos) {
        const photos = this.data.photos;
        _.forEach(photos, (photo) => {
          urls.push({
            urls: photo,
          });
        });
      }
      this.form.patchValue({
        date: this.data.expected_date,
        time: timeValue,
        type: _.get(this.data, 'type') ? this.data.type : 0,
        assignee:
          _.get(this.data, 'staff.id') || _.get(this.data, 'schedule_staff_id'),
        cleanLevel: _.get(this.data, 'level') ? this.data.level : 0,
        fee: _.get(this.data, 'fee') ? this.data.fee : 0,
        note: _.get(this.data, 'note'),
        appendix: urls,
      });
      this.checkedHouses.push(_.get(this.data, 'house.id'));
      this.hasCheckedHouses = _.cloneDeep(this.checkedHouses);
      this.defaultHouseTitle = _.get(this.data, 'house.title_alias');
    } else {
      const time = moment().startOf('day').add(12, 'hours').toDate();
      const oldSettings = this.localService.get(
        'host_arrange_clean_params',
        2
      ) || { diff: 0 };
      this.form.patchValue({
        date: moment().add(oldSettings.diff, 'days').toDate(),
        time: time,
      });
    }
  }

  checkedHousesChange(house) {
    this.checkedHouses = _.map(house, 'id');
    this.hasCheckedHouses = _.cloneDeep(this.checkedHouses);
    this.getCleanFeeForHouse();
  }

  getCleanFeeForHouse() {
    this.createTaskService
      .getCleanFee({
        house_ids: `[${this.checkedHouses}]`,
      })
      .subscribe((res) => {
        if (res.error_code === 0) {
          const cleaningInfo = _.get(res, 'data.list[0].house_cleaning');
          const fee = cleaningInfo.fee;
          this.fees = {
            standard: fee.standard ? fee.standard : 0,
            simple: fee.simple ? fee.simple : 0,
            advance: fee.advance ? fee.advance : 0,
            currency: cleaningInfo.currency_type || this.currencyList[0],
          };
          const staffForHouse = _.get(
            res,
            'data.list[0].schedule_staffs[0].id'
          );
          const value = this.form.value;

          if (this.editIniting) {
            if (!this.data.currency_type) {
              this.form.patchValue({
                currency: this.fees.currency,
              });
            }
            if (!this.data.schedule_staff_id && !_.get(this.data, 'staff')) {
              this.form.patchValue({
                assignee: staffForHouse,
              });
            }
            if (!value.type && !this.data.fee) {
              this.form.patchValue({
                fee:
                  value.cleanLevel === 0
                    ? this.fees.standard
                    : value.cleanLevel === 1
                    ? this.fees.simple
                    : this.fees.advance,
              });
            }
            this.editIniting = false;
          } else {
            const fee = value.type
              ? 0
              : value.cleanLevel === 0
              ? this.fees.standard
              : value.cleanLevel === 1
              ? this.fees.simple
              : this.fees.advance;
            const currency = value.type
              ? this.currencyList[0]
              : this.fees.currency;
            this.form.patchValue({
              fee,
              currency,
              assignee: staffForHouse,
            });
          }
        }
      });
  }

  typeChange() {
    const value = this.form.value;
    if (value.type !== 0) {
      this.form.patchValue({
        fee: 0,
      });
    } else {
      this.form.patchValue({
        fee:
          value.cleanLevel === 0
            ? this.fees.standard
            : value.cleanLevel === 1
            ? this.fees.simple
            : this.fees.advance,
        currency: this.fees.currency,
      });
    }
  }

  cleanLevelChange() {
    const value = this.form.value;
    this.form.patchValue({
      fee:
        value.cleanLevel === 0
          ? this.fees.standard
          : value.cleanLevel === 1
          ? this.fees.simple
          : this.fees.advance,
    });
  }

  toTasksFee() {
    this.router.navigate(['/app/tasks/fee']);
    this.subject.close();
  }

  save() {
    this.checking = true;
    if (!this.checkValid()) {
      return;
    }
    if (_.get(this.data, 'id')) {
      this.saveModify();
    } else {
      this.saveCreate();
    }
  }

  saveModify() {
    this.btnLoading = true;
    const value = this.form.value;
    const pics = [];
    _.forEach(value.appendix, (files) => {
      pics.push(files.urls);
    });
    const params = {
      id: +this.data.id,
      expected_date: moment(value.date).format('YYYY-MM-DD'),
      expected_time: moment(value.time).format('HH:mm:ss'),
      level: +value.cleanLevel,
      currency_type: String(value.currency),
      fee: +value.fee,
      note: value.note,
      photos: JSON.stringify(pics),
      house_id: this.checkedHouses.length === 0 ? null : this.checkedHouses[0],
      schedule_staff_id: value.assignee ? value.assignee[0] : null,
    };
    this.createTaskService.updateTask(params).subscribe((res) => {
      this.btnLoading = false;
      if (res.error_code === 0) {
        this.subject.destroy('OK');
      }
    });
  }

  saveCreate() {
    this.btnLoading = true;
    const value = this.form.value;
    const diff = moment(value.date).diff(moment().startOf('day'), 'days');
    this.localService.set(
      'host_arrange_clean_params',
      {
        diff: diff,
      },
      2
    );
    const pics = [];
    _.forEach(value.appendix, (files) => {
      pics.push(files.urls);
    });
    const params = {
      type: value.type,
      house_id: this.checkedHouses.length === 0 ? null : this.checkedHouses[0],
      schedule_staff_id: _.get(value.assignee, '[0]'),
      expected_date: moment(value.date).format('YYYY-MM-DD'),
      expected_time: moment(value.time).format('HH:mm:ss'),
      level: value.cleanLevel,
      currency_type: value.currency,
      fee: value.fee,
      note: value.note,
      gen_transaction: value.included ? 1 : null,
      payment_method: value.payment_method,
      photos: JSON.stringify(pics),
    };
    this.createTaskService.createTask(params).subscribe((res) => {
      this.btnLoading = false;
      if (res.error_code === 0) {
        this.subject.destroy('OK');
      }
    });
  }

  cancel() {
    this.subject.destroy();
  }

  checkValid() {
    const value = this.form.value;
    if (
      this.invalid(value.type) ||
      this.invalid(value.date) ||
      this.invalid(value.time) ||
      this.invalid(value.fee)
    ) {
      return false;
    }
    return true;
  }

  invalid(data) {
    if (isArray(data)) {
      return data.length === 0 ? true : false;
    }
    return data === null ? true : data === '' ? true : false;
  }
}
